/**
 * ProgressBar Component
 * Enhanced progress bar with status colors and labels
 * Used for control results, scan progress, etc.
 */

import React from 'react';
import { Progress, Space, Typography } from 'antd';
import { colors } from '../../theme/designTokens';

const { Text } = Typography;

/**
 * ProgressBar Component
 *
 * @param {number} percent - Progress percentage (0-100)
 * @param {string} status - Status type ('pass' | 'fail' | 'warning' | 'info' | 'normal')
 * @param {string} label - Label text
 * @param {boolean} showInfo - Show percentage (default: true)
 * @param {string} size - Bar size ('small' | 'default' | 'large')
 * @param {Object} style - Additional styles
 */
function ProgressBar({
  percent,
  status = 'normal',
  label,
  showInfo = true,
  size = 'default',
  style = {},
}) {
  // Map status to Ant Design Progress status and color
  const getStatusConfig = () => {
    switch (status) {
      case 'pass':
        return {
          status: 'success',
          strokeColor: colors.status.pass,
        };
      case 'fail':
        return {
          status: 'exception',
          strokeColor: colors.status.fail,
        };
      case 'warning':
        return {
          status: 'normal',
          strokeColor: colors.status.warning,
        };
      case 'info':
        return {
          status: 'normal',
          strokeColor: colors.status.info,
        };
      default:
        return {
          status: 'normal',
          strokeColor: colors.primary[500],
        };
    }
  };

  const config = getStatusConfig();

  // Size mapping
  const strokeWidth = size === 'large' ? 12 : size === 'small' ? 6 : 8;

  return (
    <div style={{ width: '100%', ...style }}>
      {label && (
        <Space style={{ marginBottom: 4 }}>
          <Text strong style={{ fontSize: size === 'small' ? 12 : 14 }}>
            {label}
          </Text>
        </Space>
      )}
      <Progress
        percent={percent}
        status={config.status}
        strokeColor={config.strokeColor}
        showInfo={showInfo}
        size={size}
        strokeWidth={strokeWidth}
      />
    </div>
  );
}

export default ProgressBar;
